import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";

const SUPPORT_EMAIL = "ron@comos-federation.com";

interface SupportCardSpec {
  title: string;
  body: string;
  ctaLabel: string;
  variant: "primary" | "outline" | "ghost";
  to?: string;
  href?: string;
  external?: boolean;
}

const CARDS: SupportCardSpec[] = [
  {
    title: "Talk to us",
    body: "Questions about platforms, tokens, agent integration, or anything else — write directly. We answer.",
    ctaLabel: SUPPORT_EMAIL,
    variant: "primary",
    href: `mailto:${SUPPORT_EMAIL}`,
  },
  {
    title: "Read the docs",
    body: "The MCP spec, every endpoint, every verb. Quick-start, per-platform reference, and the concepts the OS rests on.",
    ctaLabel: "Open docs",
    variant: "outline",
    to: "/docs",
  },
  {
    title: "Run the readiness check",
    body: "Drop in any storefront URL — Shopify, Woo, anything — and get an agent-readiness score in 30 seconds.",
    ctaLabel: "Score your store",
    variant: "outline",
    // CO 237: internal route on this site now.
    to: "/agent-ready",
  },
  {
    title: "Pricing",
    body: "Set up free. You pay only when money moves — no plan to pick, nothing charged for sitting still. Read how it works before you ask; most questions answer themselves on the pricing page.",
    ctaLabel: "See pricing",
    variant: "ghost",
    to: "/pricing",
  },
];

/**
 * SupportPage — operator-to-operator. (ComOS design system)
 *
 * Four ways in: a human, the docs, the readiness check, the pricing page.
 * No trial, no subscription — set up free, pay only when money moves.
 */
export function SupportPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* Hero */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              Support
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              Operator-to-operator.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Reach a human, or read the spec.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* Four ways in */}
      <Section tint="sunk">
        <Container>
          <motion.div
            {...inView}
            variants={stagger}
            className="grid gap-5 md:grid-cols-2"
          >
            {CARDS.map((c) => (
              <motion.div key={c.title} variants={fadeUp}>
                <Card hover className="h-full flex flex-col">
                  <h3 className="text-display-sm mb-2">{c.title}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm flex-grow">
                    {c.body}
                  </p>
                  <div className="mt-6">
                    {c.to ? (
                      <Link
                        to={c.to}
                        className={buttonStyles({ variant: c.variant })}
                      >
                        {c.ctaLabel}
                      </Link>
                    ) : (
                      <a
                        href={c.href}
                        target={c.external ? "_blank" : undefined}
                        rel={c.external ? "noopener noreferrer" : undefined}
                        className={buttonStyles({ variant: c.variant })}
                      >
                        {c.ctaLabel}
                      </a>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            {...inView}
            variants={fadeUp}
            className="mt-12 text-center"
          >
            <p className="text-mute dark:text-mute-soft text-sm mb-2">
              Building an agent? See the agent surface.
            </p>
            <Link to="/agents" className={buttonStyles({ variant: "ghost" })}>
              For agents →
            </Link>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
